import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { getUserDisplayName, getUserFullName } from "@/utils/formatDisplayName";
import { parseUtcIso } from "@/utils/dateTime";
import { useToast, PageHeader, PageHeaderWithBack } from "@/components/common";
import { ChatWindow } from "@/features/client/notifications/components/ChatWindow";
import { ClientMessagesList, getQuoteJobUnreadCount, getStaffUnreadCount, NotificationsTabContent, QuoteJobThreadsList, SelectContactModal, } from "./components";
import styles from "./MessagesPage.module.css";
const VALID_TABS = ["messages", "notifications"];
const MESSAGES_TABS = [
    { id: "messages", label: "Messages" },
    { id: "notifications", label: "Notifications" },
];
export const MessagesPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { error: showError, info: showInfo } = useToast();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("messages");
    const [messagesSubTab, setMessagesSubTab] = useState("clients");
    const [generalThreads, setGeneralThreads] = useState([]);
    const [quoteThreads, setQuoteThreads] = useState([]);
    const [jobThreads, setJobThreads] = useState([]);
    const [threadsLoading, setThreadsLoading] = useState(false);
    const [quoteThreadsLoading, setQuoteThreadsLoading] = useState(false);
    const [jobThreadsLoading, setJobThreadsLoading] = useState(false);
    const [clientSubTab, setClientSubTab] = useState("contact");
    const [selectedThread, setSelectedThread] = useState(null);
    const [threadId, setThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [selectContactModalOpen, setSelectContactModalOpen] = useState(false);
    const contactTypeUpper = (t) => t.contactType?.toUpperCase() ?? "";
    const sortByLastMessage = (a, b) => {
        const atA = a.lastMessageAt ?? "";
        const atB = b.lastMessageAt ?? "";
        return atB.localeCompare(atA);
    };
    const clientThreads = useMemo(() => generalThreads
        .filter((t) => {
        const ct = contactTypeUpper(t);
        return (ct !== "SURVEYOR" && ct !== "CONTRACTOR" && ct !== "SUBCONTRACTOR");
    })
        .sort(sortByLastMessage), [generalThreads]);
    const surveyorThreads = useMemo(() => generalThreads
        .filter((t) => contactTypeUpper(t) === "SURVEYOR")
        .sort(sortByLastMessage), [generalThreads]);
    const contractorThreads = useMemo(() => generalThreads
        .filter((t) => {
        const ct = contactTypeUpper(t);
        return ct === "CONTRACTOR" || ct === "SUBCONTRACTOR";
    })
        .sort(sortByLastMessage), [generalThreads]);
    const selectedContactId = selectedThread?.type === "general" ? selectedThread.contactId : null;
    const selectedGeneralThread = generalThreads.find((t) => t.contactId === selectedContactId);
    const contactDisplayName = useMemo(() => {
        if (!selectedThread)
            return "Client";
        if (selectedThread.type === "general") {
            return selectedGeneralThread?.contactName || selectedThread.contactId;
        }
        if (selectedThread.type === "quote") {
            const quoteThread = quoteThreads.find((t) => t.quoteId === selectedThread.quoteId && t.contactId === selectedThread.contactId);
            const num = quoteThread?.quoteNumber;
            return num
                ? `Quote #${num} - ${quoteThread?.contactName ?? "Unknown"}`
                : quoteThread?.contactName ?? "Client";
        }
        const jobThread = jobThreads.find((t) => t.jobId === selectedThread.jobId && t.contactId === selectedThread.contactId);
        const num = jobThread?.jobNumber;
        return num
            ? `Job #${num} - ${jobThread?.contactName ?? "Unknown"}`
            : jobThread?.contactName ?? "Client";
    }, [
        selectedThread,
        selectedGeneralThread,
        quoteThreads,
        jobThreads,
    ]);
    const clientsUnread = useMemo(() => clientThreads.reduce((sum, thread) => sum + getStaffUnreadCount(thread), 0), [clientThreads]);
    const surveyorUnread = useMemo(() => surveyorThreads.reduce((sum, thread) => sum + getStaffUnreadCount(thread), 0), [surveyorThreads]);
    const contractorUnread = useMemo(() => contractorThreads.reduce((sum, thread) => sum + getStaffUnreadCount(thread), 0), [contractorThreads]);
    const quoteUnread = useMemo(() => quoteThreads.reduce((sum, t) => sum + getQuoteJobUnreadCount(t), 0), [quoteThreads]);
    const jobUnread = useMemo(() => jobThreads.reduce((sum, t) => sum + getQuoteJobUnreadCount(t), 0), [jobThreads]);
    const clientsTotalUnread = useMemo(() => clientsUnread + quoteUnread + jobUnread, [clientsUnread, quoteUnread, jobUnread]);
    const sortByLastMessageDto = (a, b) => {
        const atA = a.lastMessageAt ?? "";
        const atB = b.lastMessageAt ?? "";
        return atB.localeCompare(atA);
    };
    const sortedQuoteThreads = useMemo(() => [...quoteThreads].sort(sortByLastMessageDto), [quoteThreads]);
    const sortedJobThreads = useMemo(() => [...jobThreads].sort(sortByLastMessageDto), [jobThreads]);
    const fetchGeneralThreads = useCallback(async () => {
        setThreadsLoading(true);
        try {
            const threads = await messagesService.getGeneralThreads();
            setGeneralThreads(threads);
        }
        catch {
            setGeneralThreads([]);
        }
        finally {
            setThreadsLoading(false);
        }
    }, []);
    const fetchQuoteThreads = useCallback(async () => {
        setQuoteThreadsLoading(true);
        try {
            const threads = await messagesService.getQuoteThreads();
            setQuoteThreads(threads);
        }
        catch {
            setQuoteThreads([]);
        }
        finally {
            setQuoteThreadsLoading(false);
        }
    }, []);
    const fetchJobThreads = useCallback(async () => {
        setJobThreadsLoading(true);
        try {
            const threads = await messagesService.getJobThreads();
            setJobThreads(threads);
        }
        catch {
            setJobThreads([]);
        }
        finally {
            setJobThreadsLoading(false);
        }
    }, []);
    const loadThreadForSelection = useCallback(async (selection) => {
        if (!selection)
            return;
        setThreadLoading(true);
        setThreadId(null);
        setMessages([]);
        const applyResult = (result) => {
            if (result?.thread && Array.isArray(result.messages)) {
                setThreadId(result.thread.id);
                const transformed = result.messages.map((msg) => ({
                    id: msg.id,
                    threadId: msg.threadId,
                    content: msg.content,
                    senderId: msg.senderId,
                    senderName: msg.senderName,
                    senderType: msg.senderType,
                    timestamp: parseUtcIso(msg.createdAt),
                    isRead: msg.isRead,
                }));
                const seen = new Set();
                const deduped = transformed.filter((m) => {
                    if (seen.has(m.id))
                        return false;
                    seen.add(m.id);
                    return true;
                });
                setMessages(deduped);
                const hasUnreadFromOther = deduped.some((m) => !m.isRead &&
                    (m.senderType === "client" || m.senderType === "surveyor"));
                if (hasUnreadFromOther) {
                    messagesService.markAsRead(result.thread.id).catch(() => { });
                }
            }
        };
        try {
            if (selection.type === "general") {
                let result = await messagesService.getThreadByGeneral(selection.contactId);
                if (!result) {
                    try {
                        const createRes = await messagesService.createThread({
                            contactId: selection.contactId,
                            type: "GENERAL",
                        });
                        const thread = createRes.data;
                        if (thread?.id) {
                            result = {
                                thread: thread,
                                messages: [],
                            };
                        }
                    }
                    catch (createErr) {
                        const { message } = parseCreateThreadError(createErr);
                        showError(message);
                    }
                }
                applyResult(result ?? null);
            }
            else if (selection.type === "quote") {
                let result = await messagesService.getThreadByQuote(selection.quoteId, selection.contactId);
                if (!result) {
                    try {
                        const createRes = await messagesService.createThread({
                            contactId: selection.contactId,
                            type: "QUOTE",
                            quoteId: selection.quoteId,
                        });
                        const thread = createRes.data;
                        if (thread?.id) {
                            result = {
                                thread: thread,
                                messages: [],
                            };
                        }
                    }
                    catch (createErr) {
                        const { message } = parseCreateThreadError(createErr);
                        showError(message);
                    }
                }
                applyResult(result ?? null);
            }
            else if (selection.type === "job") {
                let result = await messagesService.getThreadByJob(selection.jobId, selection.contactId);
                if (!result) {
                    try {
                        const createRes = await messagesService.createThread({
                            contactId: selection.contactId,
                            type: "JOB",
                            jobId: selection.jobId,
                        });
                        const thread = createRes.data;
                        if (thread?.id) {
                            result = {
                                thread: thread,
                                messages: [],
                            };
                        }
                    }
                    catch (createErr) {
                        const { message } = parseCreateThreadError(createErr);
                        showError(message);
                    }
                }
                applyResult(result ?? null);
            }
        }
        catch {
            setThreadId(null);
            setMessages([]);
        }
        finally {
            setThreadLoading(false);
        }
    }, [showError]);
    useEffect(() => {
        const tab = searchParams.get("tab");
        const contactIdFromParams = searchParams.get("contactId");
        const contactIdFromState = location.state?.contactId;
        const contactTypeFromState = location.state?.contactType;
        const contactId = contactIdFromParams ?? contactIdFromState;
        const ct = (contactTypeFromState ?? "").toUpperCase();
        if (tab && VALID_TABS.includes(tab)) {
            setActiveTab(tab);
            if (contactId)
                setSelectedThread({ type: "general", contactId });
            if (tab === "messages" && contactId && ct) {
                const subTab = ct === "SURVEYOR"
                    ? "surveyor"
                    : ct === "CONTRACTOR" || ct === "SUBCONTRACTOR"
                        ? "contractors"
                        : "clients";
                setMessagesSubTab(subTab);
            }
        }
        else if (tab === "surveyor" || tab === "contractors") {
            setActiveTab("messages");
            setMessagesSubTab(tab === "surveyor" ? "surveyor" : "contractors");
            if (contactId)
                setSelectedThread({ type: "general", contactId });
        }
        else if (contactId) {
            setActiveTab("messages");
            const subTab = ct === "SURVEYOR"
                ? "surveyor"
                : ct === "CONTRACTOR" || ct === "SUBCONTRACTOR"
                    ? "contractors"
                    : "clients";
            setMessagesSubTab(subTab);
            setSelectedThread({ type: "general", contactId });
        }
    }, [searchParams, location.state?.contactId, location.state?.contactType]);
    useEffect(() => {
        if (activeTab === "messages") {
            fetchGeneralThreads();
        }
    }, [activeTab, fetchGeneralThreads]);
    useEffect(() => {
        if (activeTab === "messages" && messagesSubTab === "clients") {
            fetchQuoteThreads();
        }
    }, [activeTab, messagesSubTab, fetchQuoteThreads]);
    useEffect(() => {
        if (activeTab === "messages" && messagesSubTab === "clients") {
            fetchJobThreads();
        }
    }, [activeTab, messagesSubTab, fetchJobThreads]);
    useEffect(() => {
        if (!selectedThread)
            return;
        const threadsForGeneral = messagesSubTab === "surveyor"
            ? surveyorThreads
            : messagesSubTab === "contractors"
                ? contractorThreads
                : clientThreads;
        const isValid = selectedThread.type === "general"
            ? threadsForGeneral.some((t) => t.contactId === selectedThread.contactId)
            : selectedThread.type === "quote"
                ? quoteThreads.some((t) => t.quoteId === selectedThread.quoteId &&
                    t.contactId === selectedThread.contactId)
                : jobThreads.some((t) => t.jobId === selectedThread.jobId &&
                    t.contactId === selectedThread.contactId);
        const loading = selectedThread.type === "general"
            ? threadsLoading
            : selectedThread.type === "quote"
                ? quoteThreadsLoading
                : jobThreadsLoading;
        if (!loading && !isValid) {
            setSelectedThread(null);
            setThreadId(null);
            setMessages([]);
        }
    }, [
        selectedThread,
        messagesSubTab,
        clientThreads,
        surveyorThreads,
        contractorThreads,
        quoteThreads,
        jobThreads,
        threadsLoading,
        quoteThreadsLoading,
        jobThreadsLoading,
    ]);
    useEffect(() => {
        if (activeTab === "messages" && selectedThread) {
            loadThreadForSelection(selectedThread);
        }
        else {
            setThreadId(null);
            setMessages([]);
        }
    }, [activeTab, selectedThread, loadThreadForSelection]);
    useMessagesSignalR(threadId, {
        onNewMessage: (payload) => {
            if (payload.threadId !== threadId)
                return;
            const msg = payload.message;
            const chatMessage = {
                id: msg.id,
                threadId: msg.threadId,
                content: msg.content,
                senderId: msg.senderId,
                senderName: msg.senderName,
                senderType: msg.senderType,
                timestamp: parseUtcIso(msg.createdAt),
                isRead: msg.isRead,
            };
            setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id))
                    return prev;
                return [...prev, chatMessage];
            });
            if (msg.senderId !== user?.id) {
                showInfo("New message received");
            }
            fetchGeneralThreads();
            fetchQuoteThreads();
            fetchJobThreads();
        },
        onConnectionError: () => {
            showError("Real-time updates unavailable");
        },
    });
    const handleSendMessage = async (content) => {
        if (!selectedThread || !content.trim())
            return;
        setSendingMessage(true);
        const optimistic = {
            id: `temp-${Date.now()}`,
            threadId: threadId ?? "",
            content: content.trim(),
            senderId: user?.id ?? "staff",
            senderName: getUserFullName(user, "You"),
            senderType: "staff",
            timestamp: new Date(),
            isRead: true,
        };
        setMessages((prev) => [...prev, optimistic]);
        const basePayload = {
            content: content.trim(),
            senderId: user?.id,
            senderName: getUserFullName(user, "Staff"),
        };
        try {
            const payload = selectedThread.type === "general"
                ? {
                    ...basePayload,
                    threadId: threadId ?? undefined,
                    threadType: "general",
                    contactId: selectedThread.contactId,
                    assignedcontactId: selectedThread.contactId,
                }
                : selectedThread.type === "quote"
                    ? {
                        ...basePayload,
                        threadId: threadId ?? undefined,
                        threadType: "quote",
                        quoteId: selectedThread.quoteId,
                        assignedcontactId: selectedThread.contactId,
                    }
                    : {
                        ...basePayload,
                        threadId: threadId ?? undefined,
                        threadType: "job",
                        jobId: selectedThread.jobId,
                        assignedcontactId: selectedThread.contactId,
                    };
            const response = await messagesService.sendMessage(payload);
            if (!threadId && response.data?.threadId) {
                setThreadId(response.data.threadId);
            }
            setMessages((prev) => prev.map((m) => m.id === optimistic.id
                ? {
                    ...m,
                    id: response.data.id,
                    threadId: response.data.threadId ?? m.threadId,
                    timestamp: parseUtcIso(response.data.createdAt),
                }
                : m));
        }
        catch {
            setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        }
        finally {
            setSendingMessage(false);
        }
    };
    const showChat = Boolean(selectedThread && !threadLoading);
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedThread(null);
        setThreadId(null);
        setMessages([]);
    };
    const handleMessagesSubTabChange = (subTab) => {
        setMessagesSubTab(subTab);
        setSelectedThread(null);
        setThreadId(null);
        setMessages([]);
    };
    const handleClientSubTabChange = (subTab) => {
        setClientSubTab(subTab);
        setSelectedThread(null);
        setThreadId(null);
        setMessages([]);
    };
    const handleSelectContactSuccess = useCallback((contactId, contactType) => {
        fetchGeneralThreads();
        if (contactId) {
            setSelectedThread({ type: "general", contactId });
            if (contactType) {
                const subTab = contactType === "surveyor"
                    ? "surveyor"
                    : contactType === "contractor" || contactType === "subcontractor"
                        ? "contractors"
                        : "clients";
                setMessagesSubTab(subTab);
            }
        }
    }, [fetchGeneralThreads]);
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Messages", onBack: () => navigate(-1) }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: MESSAGES_TABS.map((tab) => (_jsx("button", { type: "button", className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => handleTabChange(tab.id), role: "tab", "aria-selected": activeTab === tab.id, children: tab.label }, tab.id))) }) }), activeTab === "notifications" ? (_jsx("div", { className: styles.tabContentArea, children: _jsx(NotificationsTabContent, {}) })) : (_jsxs("div", { children: [_jsxs("div", { className: styles.selectContactContainer, children: [_jsx("button", { type: "button", className: styles.selectContactBtn, onClick: () => setSelectContactModalOpen(true), children: "Select contact" }), _jsx(SelectContactModal, { isOpen: selectContactModalOpen, onClose: () => setSelectContactModalOpen(false), onSuccess: handleSelectContactSuccess })] }), _jsxs("div", { className: styles.messagesContent, children: [_jsxs("div", { className: styles.sidebar, children: [_jsxs("div", { className: styles.sidebarHeader, children: [_jsxs("div", { className: styles.subTabs, children: [_jsxs("button", { type: "button", className: `${styles.subTab} ${messagesSubTab === "clients" ? styles.subTabActive : ""}`, onClick: () => handleMessagesSubTabChange("clients"), children: ["Clients", clientsTotalUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: clientsTotalUnread > 99 ? "99+" : clientsTotalUnread }))] }), _jsxs("button", { type: "button", className: `${styles.subTab} ${messagesSubTab === "surveyor" ? styles.subTabActive : ""}`, onClick: () => handleMessagesSubTabChange("surveyor"), children: ["Surveyors", surveyorUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: surveyorUnread > 99 ? "99+" : surveyorUnread }))] }), _jsxs("button", { type: "button", className: `${styles.subTab} ${messagesSubTab === "contractors" ? styles.subTabActive : ""}`, onClick: () => handleMessagesSubTabChange("contractors"), children: ["Contractors", contractorUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: contractorUnread > 99 ? "99+" : contractorUnread }))] })] }), messagesSubTab === "clients" && (_jsxs("div", { className: styles.clientSubTabs, children: [_jsxs("button", { type: "button", className: `${styles.clientSubTab} ${clientSubTab === "contact" ? styles.clientSubTabActive : ""}`, onClick: () => handleClientSubTabChange("contact"), children: ["Contact", clientsUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: clientsUnread > 99 ? "99+" : clientsUnread }))] }), _jsxs("button", { type: "button", className: `${styles.clientSubTab} ${clientSubTab === "quote" ? styles.clientSubTabActive : ""}`, onClick: () => handleClientSubTabChange("quote"), children: ["Quote", quoteUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: quoteUnread > 99 ? "99+" : quoteUnread }))] }), _jsxs("button", { type: "button", className: `${styles.clientSubTab} ${clientSubTab === "job" ? styles.clientSubTabActive : ""}`, onClick: () => handleClientSubTabChange("job"), children: ["Job", jobUnread > 0 && (_jsx("span", { className: styles.subTabBadge, children: jobUnread > 99 ? "99+" : jobUnread }))] })] }))] }), _jsxs("div", { className: styles.chatList, children: [_jsx("div", { className: styles.sectionTitle, children: messagesSubTab === "surveyor"
                                                    ? "Surveyors"
                                                    : messagesSubTab === "contractors"
                                                        ? "Contractors"
                                                        : clientSubTab === "quote"
                                                            ? "Quote"
                                                            : clientSubTab === "job"
                                                                ? "Job"
                                                                : "Clients" }), messagesSubTab === "clients" && clientSubTab === "contact" ? (_jsx(ClientMessagesList, { threads: clientThreads, selectedContactId: selectedContactId, onSelectContact: (id) => setSelectedThread({ type: "general", contactId: id }), loading: threadsLoading, emptyMessage: "No conversations yet." })) : messagesSubTab === "clients" && clientSubTab === "quote" ? (_jsx(QuoteJobThreadsList, { threads: sortedQuoteThreads, selectedThreadId: selectedThread?.type === "quote"
                                                    ? selectedThread.threadId
                                                    : null, onSelectThread: (thread) => setSelectedThread({
                                                    type: "quote",
                                                    quoteId: thread.quoteId,
                                                    contactId: thread.contactId,
                                                    threadId: thread.id,
                                                }), loading: quoteThreadsLoading, emptyMessage: "No quote conversations yet.", displayPrefix: "quote" })) : messagesSubTab === "clients" && clientSubTab === "job" ? (_jsx(QuoteJobThreadsList, { threads: sortedJobThreads, selectedThreadId: selectedThread?.type === "job"
                                                    ? selectedThread.threadId
                                                    : null, onSelectThread: (thread) => setSelectedThread({
                                                    type: "job",
                                                    jobId: thread.jobId,
                                                    contactId: thread.contactId,
                                                    threadId: thread.id,
                                                }), loading: jobThreadsLoading, emptyMessage: "No job conversations yet.", displayPrefix: "job" })) : (_jsx(ClientMessagesList, { threads: messagesSubTab === "surveyor"
                                                    ? surveyorThreads
                                                    : contractorThreads, selectedContactId: selectedThread?.type === "general"
                                                    ? selectedThread.contactId
                                                    : null, onSelectContact: (id) => setSelectedThread({ type: "general", contactId: id }), loading: threadsLoading, emptyMessage: messagesSubTab === "surveyor"
                                                    ? "No surveyor conversations yet."
                                                    : "No contractor conversations yet." }))] })] }), _jsx("div", { className: styles.mainContent, children: showChat ? (_jsx(ChatWindow, { threadId: threadId ??
                                        (selectedThread?.type === "quote" || selectedThread?.type === "job"
                                            ? selectedThread.threadId
                                            : selectedThread?.type === "general"
                                                ? `contact-${selectedThread.contactId}`
                                                : ""), threadTitle: contactDisplayName, messages: messages, onSendMessage: handleSendMessage, isOnline: true, isVerified: false, currentUserId: user?.id, currentUserName: getUserDisplayName(user, "You"), sending: sendingMessage })) : selectedThread && threadLoading ? (_jsx("div", { className: styles.emptyState, children: _jsx("p", { className: styles.emptyStateSubtitle, children: "Loading conversation\u2026" }) })) : (_jsxs("div", { className: styles.emptyState, children: [_jsx("img", { src: "/images/empty-state-message.png", alt: "Select a contact to start messaging", className: styles.emptyStateImage }), _jsx("h2", { className: styles.emptyStateTitle, children: messagesSubTab === "surveyor"
                                                ? "Select a surveyor"
                                                : messagesSubTab === "contractors"
                                                    ? "Select a contractor"
                                                    : clientSubTab === "quote"
                                                        ? "Select a quote conversation"
                                                        : clientSubTab === "job"
                                                            ? "Select a job conversation"
                                                            : "Select a contact" }), _jsx("p", { className: styles.emptyStateSubtitle, children: messagesSubTab === "surveyor"
                                                ? "Choose a surveyor from the list to view and send messages."
                                                : messagesSubTab === "contractors"
                                                    ? "Choose a contractor from the list to view and send messages."
                                                    : clientSubTab === "quote"
                                                        ? "Choose a quote from the list to view and send messages."
                                                        : clientSubTab === "job"
                                                            ? "Choose a job from the list to view and send messages."
                                                            : "Choose a contact from the list to view and send messages." })] })) })] })] }))] }));
};
